import { useState } from "react";
import { api } from "../../lib/api";
import { logWarn } from "../../lib/logger";
import { useData } from "../../contexts/DataContext";
import { toast } from "sonner";
import { FileDown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import ExportFilters from "./ExportFilters";

async function blobError(err) {
  if (err?.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await err.response.data.text());
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch (parseErr) {
      logWarn("Could not parse export error response:", parseErr);
    }
  }
  return "Export failed. Please try again.";
}

function buildExportPayload({ format, projectId, assignee, status, dueFrom, dueTo, includeImages }) {
  return {
    format,
    project_id: projectId === "all" ? null : projectId,
    assignee: assignee === "all" ? null : assignee,
    status: status === "all" ? null : status,
    due_from: dueFrom || null,
    due_to: dueTo || null,
    include_images: includeImages,
  };
}

function downloadBlob(res, format) {
  const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `bwn-report-${new Date().toISOString().slice(0, 10)}.${format === "pdf" ? "pdf" : "xlsx"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ExportDialog({ open, onClose }) {
  const { projects, users } = useData();
  const [format, setFormat] = useState("pdf");
  const [projectId, setProjectId] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [status, setStatus] = useState("all");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [includeImages, setIncludeImages] = useState(true);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const payload = buildExportPayload({ format, projectId, assignee, status, dueFrom, dueTo, includeImages });
      const res = await api.post("/export/report", payload, { responseType: "blob" });
      downloadBlob(res, format);
      toast.success(`${format.toUpperCase()} report downloaded`);
      onClose();
    } catch (err) {
      toast.error(await blobError(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-background" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Export Report</DialogTitle>
          <DialogDescription>Download a task report as PDF (with chat image evidence) or Excel.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <ExportFilters format={format} setFormat={setFormat} projectId={projectId} setProjectId={setProjectId}
            assignee={assignee} setAssignee={setAssignee} status={status} setStatus={setStatus}
            dueFrom={dueFrom} setDueFrom={setDueFrom} dueTo={dueTo} setDueTo={setDueTo}
            projects={projects} users={users} />
          {format === "pdf" && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Include chat images as evidence</p>
                <p className="text-xs text-muted-foreground">Embed photos from task chats & attachments in the PDF</p>
              </div>
              <Switch checked={includeImages} onCheckedChange={setIncludeImages} data-testid="export-include-images-toggle" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="export-cancel-button">Cancel</Button>
          <Button onClick={doExport} disabled={exporting} className="bg-[#E31E24] hover:bg-[#c41920] text-white" data-testid="export-download-button">
            {exporting ? <Loader2 size={15} className="mr-2 animate-spin" /> : <FileDown size={15} className="mr-2" />}
            {exporting ? "Generating…" : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
